import ReactMarkdown from "react-markdown";
import { RefObject, useEffect, useRef, useState } from "react";
import AiSupportIcon from "../../images/ai-support-icon.svg?react";
import SmallCircleAiSupportIcon from "../../images/ai-support-icon-circle-small.svg?react";
import CloseSupportChatIcon from "../../images/close-support-chat.svg?react";
import InactiveSendMessageIcon from "../../images/inactive-send-message.svg?react";
import ActiveSendMessageIcon from "../../images/active-send-message.svg?react";
import PopoutIcon from "../../images/open-in-new-tab.svg?react";
import typingGif from "../../images/typing-indicator.gif";
import { BedrockMessage, SupportChatHistory } from "@verity/api-client/types";
import styles from "./index.module.css";
import i18nPackage from "../../i18n/i18n";
import { CoreIconButton } from "../../core/button/Button";

interface SupportChatboxProps {
	sessionId: string | undefined;
	failedSubsteps: string[];
	getAiResponse: (sessionId: string, bedrockPayload: BedrockMessage[]) => Promise<{ message: string }>;
	getSupportChatHistory: (sessionId: string) => Promise<SupportChatHistory>;
	isOpen: boolean;
	setIsOpen: (isOpen: boolean) => void;
	setIsShowingSupportModal: (isShowing: boolean) => void;
}

type ChatMessage = {
	role: "user" | "assistant";
	message: string;
	timestamp: number;
};

interface SupportChatMessagesProps {
	supportChatHistory: ChatMessage[];
	isLoading: boolean;
	setIsShowingSupportModal: (show: boolean) => void;
	messagesEndRef: RefObject<HTMLDivElement>;
}

const SUPPORT_COMMAND_CONFIG: Record<string, { label: string; href?: string; isEscalate?: boolean }> = {
	"[DISABLE_EXTENSION]": {
		label: i18nPackage.t("beforeQuiz.supportChat.commandButtons.disableBrowserExtension"),
		href: "https://support.yuja.com/hc/en-us/articles/36314294460695-Disabling-a-Browser-Extension",
	},
	"[ENABLE_INCOGNITO]": {
		label: i18nPackage.t("beforeQuiz.supportChat.commandButtons.enableIncognitoMode"),
		href: "https://support.yuja.com/hc/en-us/articles/25758832063895-Enabling-Incognito-Mode-for-the-Verity-Browser-Extension",
	},
	"[DISABLE_HANDOFF]": {
		label: i18nPackage.t("beforeQuiz.supportChat.commandButtons.disableHandoff"),
		href: "https://support.yuja.com/hc/en-us/articles/35168712896791-Disabling-Handoff-on-Apple-Devices",
	},
	"[CLOSE_APPS]": {
		label: i18nPackage.t("beforeQuiz.supportChat.commandButtons.closeOpenApplications"),
		href: "https://support.yuja.com/hc/en-us/articles/32944885015063-Manually-Closing-an-Application-Using-Task-Manager",
	},
	"[HELP_CENTER]": {
		label: i18nPackage.t("beforeQuiz.supportChat.commandButtons.helpCenter"),
		href: "https://support.yuja.com/hc/en-us/p/exam-proctoring",
	},
	"[ESCALATE]": {
		label: i18nPackage.t("beforeQuiz.supportChat.commandButtons.getHelp"),
		isEscalate: true,
	},
};

const parseAiMessage = (rawText: string) => {
	let cleanText = rawText;
	const detectedCommands: string[] = [];

	for (const cmd of Object.keys(SUPPORT_COMMAND_CONFIG)) {
		if (cleanText.includes(cmd)) {
			detectedCommands.push(cmd);
			cleanText = cleanText.split(cmd).join("");
		}
	}

	cleanText = cleanText.replace(/[ \t]{2,}/g, " ").trim();
	return { text: cleanText, commands: detectedCommands };
};

const SupportChatMessages: React.FC<SupportChatMessagesProps> = ({
	supportChatHistory,
	isLoading,
	setIsShowingSupportModal,
	messagesEndRef,
}) => {
	return (
		<div className={styles.messagesArea} tabIndex={-1}>
			{supportChatHistory
				.filter((msg) => !(msg.role === "user" && msg.message.startsWith("[SYSTEM_MESSAGE]")))
				.map((msg, idx) => {
					if (msg.role === "user") {
						return (
							<div key={idx} className={styles.userMessage}>
								<ReactMarkdown>{msg.message}</ReactMarkdown>
							</div>
						);
					} else {
						const { text, commands } = parseAiMessage(msg.message);
						const processedText = text.replace(/<\/?b>/gi, "**").replace(/\[B\](.*?)\[\/B\]/gi, "**$1**");

						return (
							<div key={idx} className={styles.aiMessageWrapper}>
								{/* Ai Message Content */}
								<div className={styles.aiMessage}>
									<ReactMarkdown>{processedText}</ReactMarkdown>

									{commands && commands.length > 0 && (
										<div>
											{commands.map((cmd, i) => {
												const cmdData = SUPPORT_COMMAND_CONFIG[cmd];
												if (!cmdData) return null;

												return cmdData.isEscalate ? (
													// ESCALATE BUTTON
													<button
														key={i}
														className={styles.interactiveCommandButton}
														onClick={() => setIsShowingSupportModal(true)}
													>
														<span>{cmdData.label}</span>
													</button>
												) : (
													// HELP GUIDE LINK BUTTON
													<a
														key={i}
														href={cmdData.href}
														target="_blank"
														rel="noopener noreferrer"
														className={styles.interactiveCommandButton}
													>
														<div className={styles.buttonContentWrapper}>
															<PopoutIcon />
															<span>{cmdData.label}</span>
														</div>
													</a>
												);
											})}
										</div>
									)}
								</div>
							</div>
						);
					}
				})}

			{isLoading && (
				<div className={styles.aiMessageWrapper}>
					<div className={isLoading ? "" : styles.aiMessage} style={{ padding: "8px 16px" }}>
						<img
							src={typingGif}
							alt={i18nPackage.t("beforeQuiz.supportChat.fetchingResponse")}
							style={{ width: "40px", height: "auto", display: "block" }}
						/>
					</div>
				</div>
			)}

			<div ref={messagesEndRef} />
		</div>
	);
};

const SupportChatbox = ({
	sessionId,
	getAiResponse,
	getSupportChatHistory,
	failedSubsteps,
	isOpen,
	setIsOpen,
	setIsShowingSupportModal,
}: SupportChatboxProps) => {
	const [inputText, setInputText] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const notifiedFailedStepsRef = useRef<Set<string>>(new Set());

	// might not even need timestamp
	const [supportChatHistory, setSupportChatHistory] = useState<
		{ role: "user" | "assistant"; message: string; timestamp: number }[]
	>([
		{
			role: "assistant",
			message: i18nPackage.t("beforeQuiz.supportChat.basicGreeting"),
			timestamp: Date.now(),
		},
	]);

	const messagesEndRef = useRef<HTMLDivElement>(null);

	const handleSendMessage = async (userText: string) => {
		if (!userText.trim()) return;
		if (!sessionId) {
			console.error("No proctoring session ID found.");
			return;
		}
		setIsLoading(true);

		const newUserMsg = { role: "user" as const, message: userText, timestamp: Date.now() };
		setSupportChatHistory((prev) => [...prev, newUserMsg]);

		const bedrockPayload = [...supportChatHistory, newUserMsg].map((msg) => ({
			role: msg.role,
			content: [{ text: msg.message }],
		}));

		try {
			const aiResponse = await getAiResponse(sessionId, bedrockPayload);
			setSupportChatHistory((prev) => [
				...prev,
				{ role: "assistant", message: aiResponse.message, timestamp: Date.now() },
			]);
		} catch (error) {
			console.error("Support chat error:", error);
			setSupportChatHistory((prev) => [
				...prev,
				{
					role: "assistant",
					message: `${i18nPackage.t("beforeQuiz.supportChat.commandButtons.basicHelpMessage")} [ESCALATE]`,
					timestamp: Date.now(),
				},
			]);
		} finally {
			setIsLoading(false);
		}
	};

	const handleSubmit = () => {
		const messageToSend = inputText.trim();
		if (!messageToSend || isLoading) return;

		setInputText("");
		handleSendMessage(messageToSend);
	};

	// Auto-scroll to bottom when a new message is added
	useEffect(() => {
		if (messagesEndRef.current) {
			const scrollContainer = messagesEndRef.current.parentElement;
			if (scrollContainer) {
				scrollContainer.scrollTo({
					top: scrollContainer.scrollHeight,
					behavior: "smooth",
				});
			}
		}
	}, [supportChatHistory, isOpen]);

	// Chat history loader - runs once when sessionId becomes available
	useEffect(() => {
		if (!sessionId) return;

		let isMounted = true;
		const loadHistory = async () => {
			try {
				const response = await getSupportChatHistory(sessionId);
				if (isMounted && response.history && response.history.length > 0) {
					setSupportChatHistory((prev) => [...prev, ...response.history]);
				}
			} catch (error) {
				console.error("Failed to load chat history:", error);
			}
		};

		loadHistory();
		return () => {
			isMounted = false;
		};
	}, [sessionId]);

	// Auto trigger AI response upon opening chat when there are new failed steps
	useEffect(() => {
		if (!isOpen || !sessionId || failedSubsteps.length === 0) return;
		if (
			supportChatHistory.length === 1 &&
			supportChatHistory[0].message === i18nPackage.t("beforeQuiz.supportChat.basicGreeting")
		) {
			setSupportChatHistory([]);
		}

		const newFailedSteps = failedSubsteps.filter((step) => !notifiedFailedStepsRef.current.has(step));
		if (newFailedSteps.length > 0) {
			newFailedSteps.forEach((step) => notifiedFailedStepsRef.current.add(step));
			handleSendMessage(`[SYSTEM_MESSAGE]: user failed setup steps: ${newFailedSteps.join(", ")}.`);
		}
	}, [isOpen, sessionId]);

	const isInactive = isLoading || !inputText.trim();
	return (
		<div
			className={styles.supportChatContainer}
			style={{
				width: isOpen ? "100%" : "auto",
				height: isOpen ? "95%" : "auto",
				transition: "width 0.3s ease, height 0.3s ease",
			}}
		>
			{/* Chat Window */}
			{isOpen && (
				<div className={styles.supportChatWindow}>
					{/* Header */}
					<div className={styles.supportChatHeader}>
						{/* Left Side: Icon and Title */}
						<div className={styles.headerLeft}>
							<SmallCircleAiSupportIcon width="26" height="26" aria-hidden="true" />
							<span className={styles.headerTitle}>{i18nPackage.t("beforeQuiz.supportChat.guidedSupport")}</span>
						</div>

						{/* Right Side: Close Button */}
						<CoreIconButton
							variant="plain"
							className={styles.supportChatCloseButton}
							onClick={() => setIsOpen(false)}
							aria-label={i18nPackage.t("beforeQuiz.supportChat.closeSupportChat")}
						>
							<CloseSupportChatIcon aria-hidden="true" />
						</CoreIconButton>
					</div>

					{/* Messages Area */}
					<SupportChatMessages
						supportChatHistory={supportChatHistory}
						isLoading={isLoading}
						setIsShowingSupportModal={setIsShowingSupportModal}
						messagesEndRef={messagesEndRef}
					/>

					{/* Text Input Area */}
					<div className={styles.inputArea}>
						<div
							className={`${styles.defaultTextFieldWrapper} ${inputText.length > 0 ? styles.activeTextFieldWrapper : ""}`}
						>
							<input
								type="text"
								className={styles.textInput}
								value={inputText}
								onChange={(e) => setInputText(e.target.value)}
								onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
								placeholder={i18nPackage.t("beforeQuiz.supportChat.askAboutIssue")}
								aria-label={i18nPackage.t("beforeQuiz.supportChat.askAboutIssue")}
								disabled={isLoading}
							/>
							<CoreIconButton
								variant="plain"
								className={`${styles.sendButton} ${!isInactive ? styles.activeSendButton : ""}`}
								onClick={handleSubmit}
								disabled={isInactive}
								aria-label={i18nPackage.t("beforeQuiz.supportChat.sendMessage")}
							>
								{isInactive ?
									<InactiveSendMessageIcon aria-hidden="true" /> :
									<ActiveSendMessageIcon aria-hidden="true" />}
							</CoreIconButton>
						</div>
					</div>
				</div>
			)}

			{/* Floating Action Button (Toggle) */}
			{!isOpen && (
				<CoreIconButton
					variant="plain"
					onClick={() => setIsOpen(true)}
					aria-label={i18nPackage.t("beforeQuiz.supportChat.openGuidedSupport")}
					className={styles.floatingButton}
				>
					<AiSupportIcon aria-hidden="true" />
				</CoreIconButton>
			)}
		</div>
	);
};

export default SupportChatbox;
